// Count by example — the browser's half. The server (cs-est-takeoff-api/symbol-sweep) reads ONE drawing at a
// time; everything about WHICH drawings, in what order, and what happens to the answers lives here, pure and
// testable:
//   resolveScope        the user's scope choice (current drawing / chosen drawings / all drawings) -> the sheets to
//                       search, and the ones that cannot be searched with the reason for each
//   runCountByExample   searches the targets one after another (progress, cancel, a failing drawing never stops
//                       the others)
//   buildResultRows     one row per found symbol; "match" rows start ticked, "near" rows do not
//   markAlreadyPlaced   a found symbol that already has a placed symbol of the chosen type on it is not placed twice
//   placementsFrom      the ticked rows as placements for the canvas
// Nothing is placed until the person applies the ticked rows, and the apply is one undo step (RoomTakeoff).

import { DEFAULT_TUNABLES } from "./takeoffGeometry";
import { isBlobPointer } from "./blobStorage";

export const SCOPES = Object.freeze({ current: "current", selected: "selected", all: "all" });

export const SKIP_REASONS = Object.freeze({
  not_pdf:  "Not a PDF drawing — only vector PDFs can be searched.",
  no_scale: "No scale set — set it first so sizes can be compared.",
  rotated:  "This drawing is rotated — count by example does not support rotated drawings yet.",
  stored:   "This drawing is stored in drawing storage — count by example cannot read it yet.",
});

/** Can this drawing be searched at all? -> null when it can, else the reason key. */
export function skipReasonOf(sheet) {
  if (!sheet || !sheet.BackgroundImage || !sheet.ImageIsPdf) return "not_pdf";
  if (isBlobPointer(sheet.BackgroundImage)) return "stored";   // the server reads the file from the database; stored sets are a later phase
  if (!(sheet.ImageFtPerInch > 0)) return "no_scale";
  if (+sheet.ImageRotation) return "rotated";   // the server reads the PDF unrotated, so its geometry would be turned against the drawing
  return null;
}

/**
 * The drawings to search for a scope choice.
 *   mode 'current'  the active drawing    'selected'  the ids in selectedIds    'all'  every drawing
 * -> { targets: [sheet], skipped: [{ sheet, reason, message }] }; a drawing the user asked for but that cannot be
 * searched is skipped WITH its reason, never silently dropped.
 */
export function resolveScope({ mode, sheets = [], activeSheetId = null, selectedIds = [] } = {}) {
  const wanted = mode === SCOPES.all ? sheets
    : mode === SCOPES.selected ? sheets.filter(s => new Set(selectedIds).has(s.SheetID))
    : sheets.filter(s => s.SheetID === activeSheetId);
  const targets = [], skipped = [];
  for (const sheet of wanted) {
    const reason = skipReasonOf(sheet);
    if (reason) skipped.push({ sheet, reason, message: SKIP_REASONS[reason] });
    else targets.push(sheet);
  }
  return { targets, skipped };
}

/**
 * Search each target drawing in turn.
 *   matchCall(sheet) -> the server's match response for that drawing (the caller binds the fingerprint)
 *   onProgress({ index, total, sheet, status })   status: 'searching' | 'done' | 'failed'
 *   signal: AbortSignal — stops before the next drawing
 * -> { sheets: [{ sheet, status: 'done'|'unavailable'|'failed', matches, message?, capped?, truncated? }], cancelled }
 */
export async function runCountByExample({ targets = [], matchCall, onProgress, signal } = {}) {
  const sheets = [];
  let cancelled = false;
  for (let i = 0; i < targets.length; i++) {
    if (signal?.aborted) { cancelled = true; break; }
    const sheet = targets[i];
    onProgress?.({ index: i, total: targets.length, sheet, status: "searching" });
    try {
      const r = await matchCall(sheet);
      if (r && r.available === false) {
        sheets.push({ sheet, status: "unavailable", matches: [], message: r.message || SKIP_REASONS[r.reason] || "This drawing cannot be searched." });
      } else {
        sheets.push({ sheet, status: "done", matches: r?.matches || [], capped: !!r?.capped, truncated: !!r?.truncated });
      }
      onProgress?.({ index: i, total: targets.length, sheet, status: "done" });
    } catch (e) {
      sheets.push({ sheet, status: "failed", matches: [], message: e?.message || "The search failed." });
      onProgress?.({ index: i, total: targets.length, sheet, status: "failed" });
    }
  }
  return { sheets, cancelled };
}

/** One row per found symbol. Match = ticked, near = not ticked (a partial copy is a question, not a count). */
export function buildResultRows(sheetResults) {
  const rows = [];
  for (const sr of sheetResults || []) {
    (sr.matches || []).forEach((m, i) => rows.push({
      id: `${sr.sheet.SheetID}:${i}`, sheetId: sr.sheet.SheetID, sheetName: sr.sheet.SheetName || sr.sheet.SheetNumber || `Drawing ${sr.sheet.SheetID}`,
      x: m.x, y: m.y, rotation: m.rotation || 0, mirrored: !!m.mirrored, score: m.score, tier: m.tier, isExample: !!m.isExample,
      alreadyPlaced: false, checked: m.tier === "match",
    }));
  }
  return rows;
}

/**
 * Rows that already have a placed symbol of the chosen type on the same drawing are marked and unticked — the
 * example is often a symbol the person already placed, and placing over it would be a double count.
 *   conditions: the revision's drawn rows;   entityKey: 'item:7' | 'assembly:3' (utils/overlapCheck.entityKeyOf)
 */
export function markAlreadyPlaced(rows, conditions, entityKey, entityKeyOf, tunables = DEFAULT_TUNABLES) {
  const dist = tunables.DUPLICATE_SYMBOL_FT ?? DEFAULT_TUNABLES.DUPLICATE_SYMBOL_FT;
  const placed = [];
  for (const c of conditions || []) {
    if (c.Style !== "count" || entityKeyOf(c) !== entityKey) continue;
    let g = null;
    try { g = typeof c.GeometryJson === "string" ? JSON.parse(c.GeometryJson || "{}") : c.GeometryJson; } catch { g = null; }
    if (g && Number.isFinite(+g.x) && Number.isFinite(+g.y)) placed.push({ sheetId: c.SheetID ?? null, x: +g.x, y: +g.y });
  }
  // a symbol is placed at its own centre; a match is reported at the example's bounding-box centre, so allow a
  // little more than the duplicate distance
  const reach = Math.max(dist, 0.5);
  return rows.map(r => {
    const already = placed.some(p => p.sheetId === r.sheetId && Math.hypot(p.x - r.x, p.y - r.y) <= reach);
    return already ? { ...r, alreadyPlaced: true, checked: false } : r;
  });
}

/** The ticked rows as placements, in reading order per drawing. */
export function placementsFrom(rows) {
  return rows.filter(r => r.checked && !r.alreadyPlaced)
    .sort((a, b) => (a.sheetId - b.sheetId) || (a.y - b.y) || (a.x - b.x))
    .map(r => ({ sheetId: r.sheetId, x: r.x, y: r.y, angleDeg: r.rotation, flip: r.mirrored }));
}

/** Counts for the summary line. */
export function summarizeRows(rows) {
  const checked = rows.filter(r => r.checked && !r.alreadyPlaced).length;
  return {
    total: rows.length, checked,
    matches: rows.filter(r => r.tier === "match").length,
    near: rows.filter(r => r.tier === "near").length,
    alreadyPlaced: rows.filter(r => r.alreadyPlaced).length,
    sheets: new Set(rows.filter(r => r.checked && !r.alreadyPlaced).map(r => r.sheetId)).size,
  };
}
